import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Send,
  FileText,
  BookOpen,
  Plus,
  Mail,
  Trash2,
  Pencil,
  CheckCircle,
  Package,
  User,
  Calendar,
  Upload,
  File,
  X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResourcesTab = ({
  trainingResources,
  emailTemplates,
  clients,
  getAuthHeaders,
  fetchAllData
}) => {
  const [activeSection, setActiveSection] = useState("weekly");
  const [selectedResourceWeek, setSelectedResourceWeek] = useState({ program: "puppy_foundations", week: 1 });
  const [selectedPreCourseProgram, setSelectedPreCourseProgram] = useState("puppy_foundations");
  
  // Resource dialog state
  const [resourceOpen, setResourceOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [resourceData, setResourceData] = useState({
    title: "",
    description: "",
    program_type: "puppy_foundations",
    week_number: 1,
    resource_type: "training_resource",
    content: "",
    link_url: "",
    file_url: "",
    file_name: ""
  });
  const [uploadingFile, setUploadingFile] = useState(false);

  // Template dialog state
  const [templateEditOpen, setTemplateEditOpen] = useState(false);
  const [templateData, setTemplateData] = useState({
    program_type: "puppy_foundations",
    week_number: 1,
    subject: "",
    body: ""
  });

  // Send email dialog state
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [sendEmailData, setSendEmailData] = useState({
    client_ids: [],
    program_type: "puppy_foundations",
    week_number: 1,
    custom_message: ""
  });

  // Selected resources for individual send
  const [selectedResources, setSelectedResources] = useState([]);
  const [sendToClientOpen, setSendToClientOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [sendToClientMessage, setSendToClientMessage] = useState("");

  // Pre-course state
  const [preCourseClients, setPreCourseClients] = useState([]);
  const [sendPreCourseOpen, setSendPreCourseOpen] = useState(false);
  const [selectedPreCourseClient, setSelectedPreCourseClient] = useState(null);
  const [selectedPreCourseResources, setSelectedPreCourseResources] = useState([]);
  const [preCourseMessage, setPreCourseMessage] = useState("");
  const [sendAllPreCourse, setSendAllPreCourse] = useState(false);

  // Fetch pre-course clients when program changes
  const fetchPreCourseClients = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/pre-course/clients/${selectedPreCourseProgram}`, { headers });
      setPreCourseClients(res.data);
    } catch (error) {
      console.error("Failed to fetch pre-course clients");
    }
  }, [selectedPreCourseProgram, getAuthHeaders]);

  useEffect(() => {
    if (activeSection === "precourse") {
      fetchPreCourseClients();
    }
  }, [activeSection, selectedPreCourseProgram, fetchPreCourseClients]);

  const getResourcesForWeek = (programType, weekNumber) => {
    return trainingResources.filter(r => 
      r.program_type === programType && r.week_number === weekNumber && r.resource_type !== "pre_course"
    );
  };

  const getPreCourseResources = (programType) => {
    return trainingResources.filter(r => 
      r.program_type === programType && r.resource_type === "pre_course"
    );
  };

  const handleSaveResource = async () => {
    try {
      const headers = getAuthHeaders();
      const dataToSend = { ...resourceData };
      if (resourceData.resource_type === "pre_course") {
        dataToSend.week_number = null;
      }
      if (editingResource) {
        await axios.put(`${API}/training-resources/${editingResource.id}`, dataToSend, { headers });
        toast.success("Resource updated!");
      } else {
        await axios.post(`${API}/training-resources`, dataToSend, { headers });
        toast.success("Resource created!");
      }
      setResourceOpen(false);
      setEditingResource(null);
      setResourceData({
        title: "", description: "", program_type: "puppy_foundations",
        week_number: 1, resource_type: "training_resource", content: "", link_url: "",
        file_url: "", file_name: ""
      });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save resource");
    }
  };

  // Handle file upload for resources
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploadingFile(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/training-resources/upload-file`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" }
      });
      setResourceData(prev => ({
        ...prev,
        file_url: res.data.file_url,
        file_name: res.data.file_name
      }));
      toast.success("File uploaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploadingFile(false);
    }
  };

  // Remove uploaded file
  const handleRemoveFile = async () => {
    if (resourceData.file_url) {
      try {
        const headers = getAuthHeaders();
        const filename = resourceData.file_url.split("/").pop();
        await axios.delete(`${API}/training-resources/delete-file/${filename}`, { headers });
      } catch (error) {
        // File deletion is best-effort; resource can still be removed even if file delete fails
        console.debug('Non-critical: file deletion failed', error?.message);
      }
    }
    setResourceData(prev => ({ ...prev, file_url: "", file_name: "" }));
  };

  const handleDeleteResource = async (resourceId) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/training-resources/${resourceId}`, { headers });
      toast.success("Resource deleted!");
      setSelectedResources(prev => prev.filter(id => id !== resourceId));
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete resource");
    }
  };

  const handleSaveTemplate = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/email-templates`, templateData, { headers });
      toast.success("Email template saved!");
      setTemplateEditOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save template");
    }
  };

  const handleDeleteTemplate = async (programType, weekNumber) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/email-templates/${programType}/${weekNumber}`, { headers });
      toast.success("Email template deleted!");
      setTemplateEditOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete template - it may not exist yet");
    }
  };

  const handleSendResourceEmail = async () => {
    if (sendEmailData.client_ids.length === 0) {
      toast.error("Please select at least one client");
      return;
    }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/training-resources/send-email`, sendEmailData, { headers });
      toast.success("Resources sent to selected clients!");
      setSendEmailOpen(false);
      setSendEmailData({ client_ids: [], program_type: "puppy_foundations", week_number: 1, custom_message: "" });
    } catch (error) {
      toast.error("Failed to send emails");
    }
  };

  // Toggle resource selection
  const toggleResourceSelection = (resourceId) => {
    setSelectedResources(prev => 
      prev.includes(resourceId) 
        ? prev.filter(id => id !== resourceId)
        : [...prev, resourceId]
    );
  };

  // Send selected resources to individual client
  const handleSendSelectedToClient = async () => {
    if (!selectedClientId) {
      toast.error("Please select a client");
      return;
    }
    if (selectedResources.length === 0) {
      toast.error("Please select at least one resource");
      return;
    }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/training-resources/send-selected`, {
        resource_ids: selectedResources,
        client_id: selectedClientId,
        custom_message: sendToClientMessage
      }, { headers });
      toast.success("Resources sent successfully!");
      setSendToClientOpen(false);
      setSelectedClientId("");
      setSendToClientMessage("");
      setSelectedResources([]);
    } catch (error) {
      toast.error("Failed to send resources");
    }
  };

  // Add selected resources directly to client's homework (no email/notification)
  const handleAddToHomework = async () => {
    if (!selectedClientId) {
      toast.error("Please select a client");
      return;
    }
    if (selectedResources.length === 0) {
      toast.error("Please select at least one resource");
      return;
    }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/training-resources/add-to-homework`, {
        resource_ids: selectedResources,
        client_id: selectedClientId
      }, { headers });
      toast.success("Added to client's homework!");
      setSendToClientOpen(false);
      setSelectedClientId("");
      setSelectedResources([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add homework");
    }
  };

  // Send pre-course materials
  const handleSendPreCourse = async () => {
    if (!selectedPreCourseClient) {
      toast.error("Please select a client");
      return;
    }
    if (!sendAllPreCourse && selectedPreCourseResources.length === 0) {
      toast.error("Please select resources or choose 'Send All'");
      return;
    }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/pre-course/send`, {
        resource_ids: selectedPreCourseResources,
        client_id: selectedPreCourseClient.id,
        program_type: selectedPreCourseProgram,
        send_all: sendAllPreCourse,
        custom_message: preCourseMessage
      }, { headers });
      toast.success(`Pre-course materials sent to ${selectedPreCourseClient.name}!`);
      setSendPreCourseOpen(false);
      setSelectedPreCourseClient(null);
      setSelectedPreCourseResources([]);
      setPreCourseMessage("");
      setSendAllPreCourse(false);
      fetchPreCourseClients();
    } catch (error) {
      toast.error("Failed to send pre-course materials");
    }
  };

  // Toggle pre-course resource selection
  const togglePreCourseResource = (resourceId) => {
    setSelectedPreCourseResources(prev => 
      prev.includes(resourceId) 
        ? prev.filter(id => id !== resourceId)
        : [...prev, resourceId]
    );
  };

  const preCourseResources = useMemo(() => 
    getPreCourseResources(selectedPreCourseProgram),
    [getPreCourseResources, selectedPreCourseProgram]
  );
  
  const weeklyResources = useMemo(() => 
    getResourcesForWeek(selectedResourceWeek.program, selectedResourceWeek.week),
    [getResourcesForWeek, selectedResourceWeek.program, selectedResourceWeek.week]
  );

  // Pre-computed filtered resources to avoid recalculation in JSX
  const trainingResourcesList = useMemo(() => 
    weeklyResources.filter(r => r.resource_type === "training_resource"),
    [weeklyResources]
  );

  const homeworkResourcesList = useMemo(() => 
    weeklyResources.filter(r => r.resource_type === "homework"),
    [weeklyResources]
  );

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <Tabs value={activeSection} onValueChange={setActiveSection}>
        <TabsList className="mb-4">
          <TabsTrigger value="weekly" data-testid="weekly-resources-tab">
            <BookOpen className="w-4 h-4 mr-2" />
            Weekly Resources
          </TabsTrigger>
          <TabsTrigger value="precourse" data-testid="precourse-tab">
            <Package className="w-4 h-4 mr-2" />
            Pre-Course Materials
          </TabsTrigger>
        </TabsList>

        {/* Weekly Resources Section */}
        <TabsContent value="weekly">
          {/* Header with actions */}
          <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <div className="flex items-center gap-4">
              <Select
                value={selectedResourceWeek.program}
                onValueChange={(v) => {
                  setSelectedResourceWeek(prev => ({ ...prev, program: v }));
                  setSelectedResources([]);
                }}
              >
                <SelectTrigger className="w-48" data-testid="resources-program-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="puppy_foundations">Puppy Foundations</SelectItem>
                  <SelectItem value="puppy_development">Puppy Development</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-1">
                {[1, 2, 3, 4].map(week => (
                  <Button
                    key={week}
                    variant={selectedResourceWeek.week === week ? "default" : "outline"}
                    className={selectedResourceWeek.week === week ? "bg-olive" : ""}
                    onClick={() => {
                      setSelectedResourceWeek(prev => ({ ...prev, week }));
                      setSelectedResources([]);
                    }}
                    data-testid={`resources-week-${week}`}
                  >
                    Week {week}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {selectedResources.length > 0 && (
                <Button 
                  variant="default"
                  className="bg-olive"
                  data-testid="send-selected-btn"
                  onClick={() => setSendToClientOpen(true)}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send {selectedResources.length} Selected to Client
                </Button>
              )}
              <Button 
                variant="outline"
                data-testid="edit-email-template-btn"
                onClick={() => {
                  setTemplateData({
                    program_type: selectedResourceWeek.program,
                    week_number: selectedResourceWeek.week,
                    subject: "",
                    body: ""
                  });
                  const existing = emailTemplates.find(t => 
                    t.program_type === selectedResourceWeek.program && 
                    t.week_number === selectedResourceWeek.week
                  );
                  if (existing) {
                    setTemplateData(existing);
                  }
                  setTemplateEditOpen(true);
                }}
              >
                <Mail className="w-4 h-4 mr-2" />
                Edit Template
              </Button>
              <Button 
                variant="outline"
                data-testid="send-to-clients-btn"
                onClick={() => {
                  setSendEmailData({
                    client_ids: [],
                    program_type: selectedResourceWeek.program,
                    week_number: selectedResourceWeek.week,
                    custom_message: ""
                  });
                  setSendEmailOpen(true);
                }}
              >
                <Send className="w-4 h-4 mr-2" />
                Send Week to Clients
              </Button>
              <Button 
                className="btn-primary"
                data-testid="add-resource-btn"
                onClick={() => {
                  setEditingResource(null);
                  setResourceData({
                    title: "",
                    description: "",
                    program_type: selectedResourceWeek.program,
                    week_number: selectedResourceWeek.week,
                    resource_type: "training_resource",
                    content: "",
                    link_url: "",
                    file_url: "",
                    file_name: ""
                  });
                  setResourceOpen(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Resource
              </Button>
            </div>
          </div>

          {/* Week Title */}
          <div className="text-center py-4 bg-sand/50 rounded-lg mb-6">
            <h3 className="font-heading text-xl text-charcoal">
              {selectedResourceWeek.program === "puppy_foundations" ? "Puppy Foundations" : "Puppy Development"} - Week {selectedResourceWeek.week}
            </h3>
            {selectedResources.length > 0 && (
              <p className="text-sm text-olive mt-1">{selectedResources.length} resource(s) selected</p>
            )}
          </div>

          {/* Resources Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Training Resources */}
            <div className="card-premium">
              <h4 className="font-heading text-lg text-charcoal mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-olive" />
                Training Resources
              </h4>
              {trainingResourcesList.length === 0 ? (
                <p className="text-stone text-sm">No training resources for this week</p>
              ) : (
                <div className="space-y-3">
                  {trainingResourcesList.map(resource => (
                    <div key={resource.id} className={`bg-sand/30 p-4 rounded-lg ${selectedResources.includes(resource.id) ? 'ring-2 ring-olive' : ''}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedResources.includes(resource.id)}
                          onCheckedChange={() => toggleResourceSelection(resource.id)}
                          data-testid={`select-resource-${resource.id}`}
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <h5 className="font-medium text-charcoal">{resource.title}</h5>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => { setEditingResource(resource); setResourceData({...resource, file_url: resource.file_url || "", file_name: resource.file_name || ""}); setResourceOpen(true); }}>
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteResource(resource.id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                          {resource.description && <p className="text-stone text-sm mb-2">{resource.description}</p>}
                          <p className="text-charcoal text-sm whitespace-pre-wrap line-clamp-3">{resource.content}</p>
                          <div className="flex flex-wrap gap-3 mt-2">
                            {resource.file_url && (
                              <a href={`${process.env.REACT_APP_BACKEND_URL}${resource.file_url}`} target="_blank" rel="noreferrer" className="text-olive text-sm hover:underline inline-flex items-center gap-1">
                                <File className="w-3 h-3" />
                                {resource.file_name || "Download File"}
                              </a>
                            )}
                            {resource.link_url && (
                              <a href={resource.link_url} target="_blank" rel="noreferrer" className="text-olive text-sm hover:underline inline-block">
                                View Link →
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Homework */}
            <div className="card-premium">
              <h4 className="font-heading text-lg text-charcoal mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-olive" />
                Homework
              </h4>
              {homeworkResourcesList.length === 0 ? (
                <p className="text-stone text-sm">No homework for this week</p>
              ) : (
                <div className="space-y-3">
                  {homeworkResourcesList.map(resource => (
                    <div key={resource.id} className={`bg-sand/30 p-4 rounded-lg ${selectedResources.includes(resource.id) ? 'ring-2 ring-olive' : ''}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedResources.includes(resource.id)}
                          onCheckedChange={() => toggleResourceSelection(resource.id)}
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <h5 className="font-medium text-charcoal">{resource.title}</h5>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => { setEditingResource(resource); setResourceData({...resource, file_url: resource.file_url || "", file_name: resource.file_name || ""}); setResourceOpen(true); }}>
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteResource(resource.id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                          {resource.description && <p className="text-stone text-sm mb-2">{resource.description}</p>}
                          <p className="text-charcoal text-sm whitespace-pre-wrap line-clamp-3">{resource.content}</p>
                          <div className="flex flex-wrap gap-3 mt-2">
                            {resource.file_url && (
                              <a href={`${process.env.REACT_APP_BACKEND_URL}${resource.file_url}`} target="_blank" rel="noreferrer" className="text-olive text-sm hover:underline inline-flex items-center gap-1">
                                <File className="w-3 h-3" />
                                {resource.file_name || "Download File"}
                              </a>
                            )}
                            {resource.link_url && (
                              <a href={resource.link_url} target="_blank" rel="noreferrer" className="text-olive text-sm hover:underline inline-block">
                                View Link →
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Pre-Course Materials Section */}
        <TabsContent value="precourse">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div className="flex items-center gap-4">
                <Select
                  value={selectedPreCourseProgram}
                  onValueChange={(v) => {
                    setSelectedPreCourseProgram(v);
                    setSelectedPreCourseResources([]);
                  }}
                >
                  <SelectTrigger className="w-48" data-testid="precourse-program-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="puppy_foundations">Puppy Foundations</SelectItem>
                    <SelectItem value="puppy_development">Puppy Development</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                className="btn-primary"
                data-testid="add-precourse-btn"
                onClick={() => {
                  setEditingResource(null);
                  setResourceData({
                    title: "",
                    description: "",
                    program_type: selectedPreCourseProgram,
                    week_number: null,
                    resource_type: "pre_course",
                    content: "",
                    link_url: "",
                    file_url: "",
                    file_name: ""
                  });
                  setResourceOpen(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Pre-Course Material
              </Button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Pre-Course Materials List */}
              <div className="card-premium">
                <h4 className="font-heading text-lg text-charcoal mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-olive" />
                  Pre-Course Materials
                  <Badge variant="secondary" className="ml-2">{preCourseResources.length}</Badge>
                </h4>
                {preCourseResources.length === 0 ? (
                  <p className="text-stone text-sm">No pre-course materials yet. Add your first one!</p>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {preCourseResources.map(resource => (
                      <div key={resource.id} className="bg-sand/30 p-4 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <h5 className="font-medium text-charcoal">{resource.title}</h5>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => { setEditingResource(resource); setResourceData({...resource, week_number: null, file_url: resource.file_url || "", file_name: resource.file_name || ""}); setResourceOpen(true); }}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteResource(resource.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        {resource.description && <p className="text-stone text-sm mb-2">{resource.description}</p>}
                        <p className="text-charcoal text-sm whitespace-pre-wrap line-clamp-3">{resource.content}</p>
                        <div className="flex flex-wrap gap-3 mt-2">
                          {resource.file_url && (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${resource.file_url}`} target="_blank" rel="noreferrer" className="text-olive text-sm hover:underline inline-flex items-center gap-1">
                              <File className="w-3 h-3" />
                              {resource.file_name || "Download File"}
                            </a>
                          )}
                          {resource.link_url && (
                            <a href={resource.link_url} target="_blank" rel="noreferrer" className="text-olive text-sm hover:underline inline-block">
                              View Link →
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Enrolled Clients */}
              <div className="card-premium">
                <h4 className="font-heading text-lg text-charcoal mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-olive" />
                  Enrolled Clients (Upcoming Intakes)
                  <Badge variant="secondary" className="ml-2">{preCourseClients.length}</Badge>
                </h4>
                {preCourseClients.length === 0 ? (
                  <p className="text-stone text-sm">No clients enrolled in upcoming {selectedPreCourseProgram === "puppy_foundations" ? "Puppy Foundations" : "Puppy Development"} intakes.</p>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {preCourseClients.map(client => (
                      <div key={client.id} className="bg-sand/30 p-4 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="font-medium text-charcoal">{client.name}</h5>
                            <p className="text-stone text-sm">{client.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Calendar className="w-3 h-3 text-olive" />
                              <span className="text-xs text-stone">
                                Starts: {client.intake_start_date} ({client.intake_day})
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            {client.total_sent > 0 ? (
                              <div className="flex items-center gap-1 text-green-600 mb-2">
                                <CheckCircle className="w-4 h-4" />
                                <span className="text-xs">{client.total_sent} sent</span>
                              </div>
                            ) : (
                              <Badge variant="outline" className="mb-2">Not sent</Badge>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setSelectedPreCourseClient(client);
                                setSelectedPreCourseResources([]);
                                setSendAllPreCourse(false);
                                setPreCourseMessage("");
                                setSendPreCourseOpen(true);
                              }}
                            >
                              <Send className="w-3 h-3 mr-1" />
                              Send
                            </Button>
                          </div>
                        </div>
                        {client.last_sent_at && (
                          <p className="text-xs text-stone mt-2">
                            Last sent: {new Date(client.last_sent_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Resource Dialog */}
      <Dialog open={resourceOpen} onOpenChange={setResourceOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingResource ? "Edit Resource" : "Add Resource"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={resourceData.title}
                onChange={(e) => setResourceData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Resource title"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Program</Label>
                <Select
                  value={resourceData.program_type}
                  onValueChange={(v) => setResourceData(prev => ({ ...prev, program_type: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="puppy_foundations">Puppy Foundations</SelectItem>
                    <SelectItem value="puppy_development">Puppy Development</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {resourceData.resource_type !== "pre_course" && (
                <div className="space-y-2">
                  <Label>Week</Label>
                  <Select
                    value={resourceData.week_number?.toString() || "1"}
                    onValueChange={(v) => setResourceData(prev => ({ ...prev, week_number: parseInt(v) }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Week 1</SelectItem>
                      <SelectItem value="2">Week 2</SelectItem>
                      <SelectItem value="3">Week 3</SelectItem>
                      <SelectItem value="4">Week 4</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={resourceData.resource_type}
                onValueChange={(v) => setResourceData(prev => ({ ...prev, resource_type: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="training_resource">Training Resource</SelectItem>
                  <SelectItem value="homework">Homework</SelectItem>
                  <SelectItem value="pre_course">Pre-Course Material</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                value={resourceData.description}
                onChange={(e) => setResourceData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description"
              />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={resourceData.content}
                onChange={(e) => setResourceData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Full content or instructions..."
                rows={6}
              />
            </div>
            
            {/* File Upload Section */}
            <div className="space-y-2">
              <Label>Upload Document (Optional)</Label>
              {resourceData.file_url ? (
                <div className="flex items-center gap-2 p-3 bg-sand/50 rounded-lg">
                  <File className="w-5 h-5 text-olive" />
                  <span className="flex-1 text-sm text-charcoal truncate">{resourceData.file_name}</span>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={handleRemoveFile}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="file"
                    id="resource-file-upload"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                  />
                  <label
                    htmlFor="resource-file-upload"
                    className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed border-olive/30 rounded-lg cursor-pointer hover:border-olive/60 hover:bg-sand/30 transition-colors ${uploadingFile ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Upload className="w-5 h-5 text-olive" />
                    <span className="text-sm text-stone">
                      {uploadingFile ? "Uploading..." : "Click to upload PDF, DOC, or image"}
                    </span>
                  </label>
                  <p className="text-xs text-stone mt-1">Max file size: 10MB</p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>External Link URL (Optional)</Label>
              <Input
                value={resourceData.link_url}
                onChange={(e) => setResourceData(prev => ({ ...prev, link_url: e.target.value }))}
                placeholder="https://..."
              />
            </div>
            <Button onClick={handleSaveResource} className="btn-primary w-full" disabled={uploadingFile}>
              {editingResource ? "Save Changes" : "Create Resource"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Email Template Dialog */}
      <Dialog open={templateEditOpen} onOpenChange={setTemplateEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Email Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-sand/50 rounded-lg p-3 text-sm">
              <strong>{templateData.program_type === "puppy_foundations" ? "Puppy Foundations" : "Puppy Development"}</strong> - Week {templateData.week_number}
            </div>
            <div className="space-y-2">
              <Label>Email Subject</Label>
              <Input
                value={templateData.subject}
                onChange={(e) => setTemplateData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Email subject line"
              />
            </div>
            <div className="space-y-2">
              <Label>Email Body</Label>
              <Textarea
                value={templateData.body}
                onChange={(e) => setTemplateData(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Email content..."
                rows={8}
              />
              <p className="text-xs text-stone">Use {"{client_name}"} to insert the client's name</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveTemplate} className="btn-primary flex-1">
                Save Template
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Email Template?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will reset to the default template.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleDeleteTemplate(templateData.program_type, templateData.week_number)} className="bg-red-600 hover:bg-red-700">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Week to All Clients Dialog */}
      <Dialog open={sendEmailOpen} onOpenChange={setSendEmailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Week Resources to Clients</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-sand/50 rounded-lg p-3 text-sm">
              Sending: <strong>{sendEmailData.program_type === "puppy_foundations" ? "Puppy Foundations" : "Puppy Development"}</strong> - Week {sendEmailData.week_number} resources
            </div>
            <div className="space-y-2">
              <Label>Select Clients</Label>
              <div className="max-h-48 overflow-y-auto border border-olive/20 rounded-lg p-2 space-y-1">
                {clients.map(client => (
                  <label key={client.id} className="flex items-center gap-2 p-2 hover:bg-sand/30 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendEmailData.client_ids.includes(client.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSendEmailData(prev => ({ ...prev, client_ids: [...prev.client_ids, client.id] }));
                        } else {
                          setSendEmailData(prev => ({ ...prev, client_ids: prev.client_ids.filter(id => id !== client.id) }));
                        }
                      }}
                      className="rounded border-olive"
                    />
                    <span className="text-charcoal">{client.name}</span>
                    <span className="text-stone text-sm">({client.email})</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-stone">{sendEmailData.client_ids.length} client(s) selected</p>
            </div>
            <div className="space-y-2">
              <Label>Additional Message (Optional)</Label>
              <Textarea
                value={sendEmailData.custom_message}
                onChange={(e) => setSendEmailData(prev => ({ ...prev, custom_message: e.target.value }))}
                placeholder="Add a personal note..."
                rows={3}
              />
            </div>
            <Button onClick={handleSendResourceEmail} className="btn-primary w-full" disabled={sendEmailData.client_ids.length === 0}>
              <Send className="w-4 h-4 mr-2" />
              Send to {sendEmailData.client_ids.length} Client(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Selected Resources to Individual Client Dialog */}
      <Dialog open={sendToClientOpen} onOpenChange={setSendToClientOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Resources to Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-sand/50 rounded-lg p-3 text-sm">
              <strong>{selectedResources.length}</strong> resource(s) selected
            </div>
            <div className="space-y-2">
              <Label>Select Client</Label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a client..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name} ({client.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Message (Optional - only for email)</Label>
              <Textarea
                value={sendToClientMessage}
                onChange={(e) => setSendToClientMessage(e.target.value)}
                placeholder="Add a personal note..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={handleAddToHomework} variant="outline" className="btn-outline" disabled={!selectedClientId}>
                <Plus className="w-4 h-4 mr-2" />
                Add to Homework Only
              </Button>
              <Button onClick={handleSendSelectedToClient} className="btn-primary" disabled={!selectedClientId}>
                <Send className="w-4 h-4 mr-2" />
                Send Email + Add
              </Button>
            </div>
            <p className="text-xs text-stone text-center">
              "Add to Homework Only" adds to client's dashboard without sending email
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Pre-Course Materials Dialog */}
      <Dialog open={sendPreCourseOpen} onOpenChange={setSendPreCourseOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Pre-Course Materials</DialogTitle>
          </DialogHeader>
          {selectedPreCourseClient && (
            <div className="space-y-4 mt-4">
              <div className="bg-sand/50 rounded-lg p-3">
                <p className="font-medium text-charcoal">{selectedPreCourseClient.name}</p>
                <p className="text-sm text-stone">{selectedPreCourseClient.email}</p>
                <p className="text-xs text-stone mt-1">
                  Course starts: {selectedPreCourseClient.intake_start_date}
                </p>
                {selectedPreCourseClient.total_sent > 0 && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Already sent {selectedPreCourseClient.total_sent} material(s)
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Select Materials</Label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={sendAllPreCourse}
                      onCheckedChange={(checked) => {
                        setSendAllPreCourse(checked);
                        if (checked) setSelectedPreCourseResources([]);
                      }}
                    />
                    Send All
                  </label>
                </div>
                {!sendAllPreCourse && (
                  <div className="max-h-48 overflow-y-auto border border-olive/20 rounded-lg p-2 space-y-1">
                    {preCourseResources.map(resource => (
                      <label key={resource.id} className="flex items-center gap-2 p-2 hover:bg-sand/30 rounded cursor-pointer">
                        <Checkbox
                          checked={selectedPreCourseResources.includes(resource.id)}
                          onCheckedChange={() => togglePreCourseResource(resource.id)}
                        />
                        <span className="text-charcoal flex-1">{resource.title}</span>
                        {selectedPreCourseClient.sent_resource_ids?.includes(resource.id) && (
                          <Badge variant="outline" className="text-xs">Already sent</Badge>
                        )}
                      </label>
                    ))}
                  </div>
                )}
                {!sendAllPreCourse && (
                  <p className="text-xs text-stone">{selectedPreCourseResources.length} material(s) selected</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Personal Message (Optional)</Label>
                <Textarea
                  value={preCourseMessage}
                  onChange={(e) => setPreCourseMessage(e.target.value)}
                  placeholder="Add a welcome note..."
                  rows={3}
                />
              </div>

              <Button 
                onClick={handleSendPreCourse} 
                className="btn-primary w-full" 
                disabled={!sendAllPreCourse && selectedPreCourseResources.length === 0}
              >
                <Send className="w-4 h-4 mr-2" />
                {sendAllPreCourse ? `Send All (${preCourseResources.length})` : `Send ${selectedPreCourseResources.length} Material(s)`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
